import i18n from 'i18next';

import { ProjectStatus } from '@/modules/projects/types/project';
import { ConflictFieldConfig } from '@/modules/projects/types/project-conflicts';
import { formatDateToLocalFormat, formatDisplayDate } from '@/utils/date-formatters';

/**
 * Field keys for project info conflict resolution
 * Uses form field names as keys for consistency
 */
export type ProjectInfoFieldKey =
  | 'name'
  | 'status'
  | 'probability'
  | 'color'
  | 'clientId'
  | 'managerIds'
  | 'startDate'
  | 'projectEndDate'
  | 'description'
  | 'industryIds'
  | 'customAttributes';

/**
 * Field configurations for project info conflict resolution
 * Defines how each field should be displayed in the conflicts table
 *
 * Rules:
 * 1. Fields with alwaysExcluded=true are never shown (name, status, color, client, projectEndDate, description, customAttributes)
 * 2. Other fields are shown only if values differ between form and any snapshot
 * 3. snapshotKey maps form field names to snapshot field names when they differ
 * 4. getFormValueForComparison/getSnapshotValueForComparison handle value transformation for comparison
 */
export const projectInfoConflictFields: ConflictFieldConfig<ProjectInfoFieldKey>[] = [
  {
    key: 'name',
    labelKey: 'conflictFields.info.name',
    alwaysExcluded: true,
  },
  {
    key: 'status',
    labelKey: 'conflictFields.info.status',
    alwaysExcluded: false,
  },
  {
    key: 'probability',
    snapshotKey: 'tentativeProbability',
    labelKey: 'conflictFields.info.probability',
    // Both snapshot data (via project-conflicts.mapper.ts) and form values use 0-100 scale.
    // No normalization needed here.
    valueFormatter: (value: number | null) => {
      if (value === null || value === undefined) return i18n.t('common:table.noValue');
      return `${value}%`;
    },
    // Probability is only applicable when status is TENTATIVE
    isDisabled: (selectedValues: Record<string, any>) => {
      return selectedValues.status !== ProjectStatus.Tentative;
    },
    // Null probability is not a valid selection for tentative projects
    excludeNullOption: true,
  },
  {
    key: 'color',
    snapshotKey: 'colorHex',
    labelKey: 'conflictFields.info.colorHex',
    alwaysExcluded: true,
  },
  {
    key: 'clientId',
    snapshotKey: 'client',
    labelKey: 'conflictFields.info.clientId',
    alwaysExcluded: true,
  },
  {
    key: 'managerIds',
    snapshotKey: 'managers',
    labelKey: 'conflictFields.info.managerId',
    // currentFormValues passes [{ id, name }, ...] for this field; snapshot has Manager[]
    // Compare by sorted comma-joined id strings
    getFormValueForComparison: (
      formValue: Array<{ id?: number | string; name?: string }> | null
    ) => {
      if (!Array.isArray(formValue) || formValue.length === 0) return null;
      return formValue
        .map(m => m.id?.toString())
        .filter(Boolean)
        .sort()
        .join(',');
    },
    getSnapshotValueForComparison: (
      snapshotValue: Array<{ id?: number; name?: string }> | null
    ) => {
      if (!Array.isArray(snapshotValue) || snapshotValue.length === 0) return null;
      return snapshotValue
        .map(m => m.id?.toString())
        .filter(Boolean)
        .sort()
        .join(',');
    },
    valueFormatter: (value: Array<{ id?: number | string; name?: string }> | null) => {
      if (!Array.isArray(value) || value.length === 0) return i18n.t('common:table.noValue');
      return value
        .map(m => m.name)
        .filter(Boolean)
        .join(', ');
    },
    // Convert [{ id, name }, ...] back to the form's string[] when resolution is applied
    resolvedValueToFormValue: (value: Array<{ id?: number | string; name?: string }> | null) => {
      if (!Array.isArray(value)) return [];
      return value.map(m => m.id?.toString()).filter(Boolean);
    },
  },
  {
    key: 'startDate',
    snapshotKey: 'projectStartDate',
    labelKey: 'conflictFields.info.projectStartDate',
    // Form has Date object, snapshot has ISO string
    // CRITICAL: Compare dates in the SAME timezone (local) to avoid false conflicts
    getFormValueForComparison: (formValue: Date | undefined) => {
      if (!formValue) return null;
      return formatDateToLocalFormat(formValue);
    },
    // Snapshot has ISO string, convert to local date for comparison
    getSnapshotValueForComparison: (snapshotValue: string | null | undefined) => {
      if (!snapshotValue) return null;
      return formatDateToLocalFormat(snapshotValue);
    },
    valueFormatter: (value: Date | string | null) => {
      if (!value) return i18n.t('common:table.noValue');
      const date = typeof value === 'string' ? new Date(value) : value;
      return formatDisplayDate(date);
    },
  },
  {
    key: 'projectEndDate',
    labelKey: 'conflictFields.info.projectEndDate',
    alwaysExcluded: true,
  },
  {
    key: 'description',
    labelKey: 'conflictFields.info.description',
    alwaysExcluded: true,
  },
  {
    key: 'industryIds',
    snapshotKey: 'industries',
    labelKey: 'conflictFields.info.industries',
    valueFormatter: value => {
      if (!value || !Array.isArray(value) || value.length === 0)
        return i18n.t('common:table.noValue');
      return value.join(', ');
    },
  },
  {
    key: 'customAttributes',
    snapshotKey: 'customAttributesValues',
    labelKey: 'conflictFields.info.customAttributes',
    alwaysExcluded: true,
  },
];
